import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Users, CalendarDays, Calendar, FileText, Wrench, LogOut, Sparkles, UserCog, FolderTree, Settings as SettingsIcon, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api, fileUrl } from "@/lib/api";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/admin/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
    { to: "/admin/customers", label: "Customers", icon: Users, testid: "nav-customers" },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarDays, testid: "nav-bookings" },
    { to: "/admin/calendar", label: "Calendar", icon: Calendar, testid: "nav-calendar" },
    { to: "/admin/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
    { to: "/admin/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
];

const ADMIN_NAV = [
    { to: "/admin/categories", label: "Categories", icon: FolderTree, testid: "nav-categories" },
    { to: "/admin/users", label: "Users", icon: UserCog, testid: "nav-users" },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [settings, setSettings] = useState({ business_name: "The Wedding Rentals" });

    useEffect(() => {
        api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
    }, []);

    const handleLogout = async () => {
        await logout();
        nav("/login");
    };

    return (
        <div className="min-h-screen flex bg-background">
            <aside className="hidden md:flex md:flex-col w-64 bg-muted/60 border-r border-border" data-testid="sidebar">
                <div className="px-6 py-7 border-b border-border">
                    <NavLink to="/admin" end className="flex items-center gap-2.5 group" data-testid="brand-logo">
                        {settings.logo_path ? (
                            <img src={fileUrl(settings.logo_path)} alt="" className="h-9 w-9 object-contain rounded-sm" />
                        ) : (
                            <Sparkles className="h-5 w-5 text-primary" strokeWidth={1.5} />
                        )}
                        <div>
                            <div className="font-serif text-lg tracking-tight leading-tight">{settings.business_name}</div>
                            <div className="label-cap mt-1">Admin</div>
                        </div>
                    </NavLink>
                </div>

                <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
                    {NAV.map(({ to, label, icon: Icon, end, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={testid}
                            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                        >
                            <Icon className="h-4 w-4" strokeWidth={1.5} />
                            <span>{label}</span>
                        </NavLink>
                    ))}

                    {user?.role === "admin" && (
                        <>
                            <div className="label-cap px-4 pt-6 pb-2">Admin</div>
                            {ADMIN_NAV.map(({ to, label, icon: Icon, testid }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    data-testid={testid}
                                    className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                                >
                                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                                    <span>{label}</span>
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>

                <div className="border-t border-border px-4 py-4">
                    <NavLink to="/" className="sidebar-link mb-3 gap-3" data-testid="view-storefront">
                        <Store className="h-4 w-4" strokeWidth={1.5} />
                        <span>View storefront</span>
                    </NavLink>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-serif text-base">
                            {(user?.name || "U")[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{user?.role}</div>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="w-full rounded-sm border-border bg-transparent hover:bg-background gap-2"
                    >
                        <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Sign out
                    </Button>
                </div>
            </aside>

            <main className="flex-1 min-w-0 overflow-x-auto">
                <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] mx-auto">
                    {children}
                </div>
            </main>
        </div>
    );
}
